/**
 * Admin › Audit log — DESIGN.md §6.10, card #25.
 *
 * "Administrative function to track e.g. logins, changes, etc etc in an
 * immutable append only audit log."
 *
 * The log itself already existed and always has: §1.2 makes every mutation an
 * event, written in the same transaction as the change, and a trigger rejects
 * UPDATE and DELETE so the append-only claim is enforced by the database
 * rather than promised by the code. What was missing was a way to *read* it
 * across the workspace — the feeds are per-card and per-project — and logins,
 * which change nothing and so were never events at all.
 *
 * Deliberately read-only. There is no export button and no delete: the export
 * surface is §12's own, and a log with a delete button is not a log.
 */
import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ScrollText } from 'lucide-react';
import { ENTITY_TYPES, EVENT_TYPES, type ActivityEvent } from '@slate/shared';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingRegion } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { ActivityLine } from '@/components/cards/activity-line';
import { useAuditLog, type AuditFilters } from '@/features/activity/api';
import { AUDIT_GROUPS } from '@/features/activity/audit-groups';
import { useUserRefs } from '@/features/lookups/api';
import { useAuth } from '@/lib/auth';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAuditPage,
});

const ANY = '__any__';

function AdminAuditPage(): JSX.Element {
  const { me } = useAuth();
  const people = useUserRefs();

  const [actorId, setActorId] = useState<string>(ANY);
  const [actorType, setActorType] = useState<string>(ANY);
  const [group, setGroup] = useState<string>(ANY);
  const [entityType, setEntityType] = useState<string>(ANY);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  const filters = useMemo<AuditFilters>(() => {
    const chosen = AUDIT_GROUPS.find((candidate) => candidate.label === group);
    return {
      ...(actorId !== ANY ? { actorId } : {}),
      ...(actorType !== ANY ? { actorType: [actorType] } : {}),
      ...(chosen ? { eventType: EVENT_TYPES.filter(chosen.match) } : {}),
      ...(entityType !== ANY ? { entityType: [entityType] } : {}),
      // A date input gives a local calendar day; the API wants an instant, and
      // the bounds are inclusive-from / exclusive-to, so "to" is the following
      // midnight rather than the same one.
      ...(from ? { from: new Date(`${from}T00:00:00`).toISOString() } : {}),
      ...(to ? { to: new Date(`${to}T00:00:00`).toISOString() } : {}),
    };
  }, [actorId, actorType, group, entityType, from, to]);

  const log = useAuditLog(filters);
  const events: ActivityEvent[] = log.data?.pages.flatMap((page) => page.items) ?? [];
  const total = log.data?.pages[0]?.totalCount ?? 0;
  const filtered =
    actorId !== ANY ||
    actorType !== ANY ||
    group !== ANY ||
    entityType !== ANY ||
    from !== '' ||
    to !== '';

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground max-w-2xl text-sm">
        Every recorded action in the workspace, newest first. The log is append-only — the database
        refuses updates and deletes to it, including from an administrator — so this is the record,
        not a view of it. Sign-ins and refused sign-ins are here alongside changes.{' '}
        <strong className="text-foreground font-medium">
          An agent&rsquo;s work carries both names
        </strong>{' '}
        — the agent that did it and the person who authorised it — so filtering by a person includes
        what their agents did for them. Use <em>Acting as</em> to separate the two.
      </p>

      <div className="bg-card grid gap-3 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        <div className="space-y-1.5">
          <Label htmlFor="audit-actor">Who</Label>
          {/* #19 — the whole staff list, and the one filter on this page most
              worth typing at. */}
          <Combobox
            id="audit-actor"
            label="Filter by person"
            className="h-9"
            value={actorId}
            options={[
              { value: ANY, label: 'Anyone' },
              ...(people.data ?? []).map((person) => ({
                value: person.id,
                label: person.name,
                keywords: [person.email],
                description: person.email,
              })),
            ]}
            onChange={setActorId}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="audit-actor-type">Acting as</Label>
          {/*
            #76 — the axis `actorId` cannot express.
            §12.5 audits an agent's write against the person who authorised it,
            which is the right call: a token must not be able to launder an
            action away from its owner. The consequence is that "Who: Brad"
            returns what Brad did *and* what Brad's agents did on his behalf,
            with no way to separate them — and no way at all to ask "what have
            the agents been doing" across everybody.

            Both are one dropdown now, and it intersects with Who rather than
            replacing it: leave Who on Anyone for every agent in the
            workspace, or set both for one person's.
          */}
          <Combobox
            id="audit-actor-type"
            label="Filter by how the action was taken"
            className="h-9"
            // Wider than the trigger: these five need their second line to be
            // useful, and at the trigger's width both the label and the
            // description truncated to an ellipsis.
            contentClassName="min-w-[19rem]"
            value={actorType}
            options={[
              { value: ANY, label: 'Any way' },
              {
                value: 'user',
                label: 'A person, directly',
                description: 'Signed in, in the browser or the API',
              },
              {
                value: 'agent',
                label: 'An agent, e.g. Claude',
                description: 'Through an MCP token, acting for someone',
              },
              {
                value: 'automation',
                label: 'An automation rule',
                description: 'A rule in Admin › Automations fired',
              },
              {
                value: 'system',
                label: 'Slate itself',
                description: 'Recurrence, due-soon, and other unattended work',
              },
            ]}
            onChange={setActorType}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="audit-group">What happened</Label>
          <Combobox
            id="audit-group"
            label="Filter by kind of event"
            className="h-9"
            value={group}
            options={[
              { value: ANY, label: 'Everything' },
              ...AUDIT_GROUPS.map((candidate) => ({
                value: candidate.label,
                label: candidate.label,
              })),
            ]}
            onChange={setGroup}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="audit-entity">On what</Label>
          <Combobox
            id="audit-entity"
            label="Filter by entity type"
            className="h-9 capitalize"
            value={entityType}
            options={[
              { value: ANY, label: 'Anything' },
              ...ENTITY_TYPES.map((type) => ({ value: type, label: type.replace(/_/g, ' ') })),
            ]}
            onChange={setEntityType}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="audit-from">From</Label>
          <Input
            id="audit-from"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="audit-to">To (before)</Label>
          <Input
            id="audit-to"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
      </div>

      {log.isLoading ? (
        <LoadingRegion label="Loading the audit log" />
      ) : events.length === 0 ? (
        <EmptyState
          icon={<ScrollText className="size-8" />}
          title={filtered ? 'Nothing matches those filters' : 'Nothing has been recorded yet'}
          description={
            filtered
              ? 'Widen the range, or set Who, Acting as and What happened back to their Any options.'
              : 'Every change, and every sign-in, appears here as it happens.'
          }
        />
      ) : (
        <>
          <p className="text-muted-foreground text-xs" role="status">
            {total.toLocaleString()} {total === 1 ? 'event' : 'events'}
            {filtered ? ' match' : ' recorded'} · showing {events.length.toLocaleString()}
          </p>
          <div className="bg-card rounded-lg border">
            <ul className="divide-border divide-y px-3" data-testid="audit-log">
              {events.map((event) => (
                <ActivityLine key={event.id} event={event} timezone={me?.timezone} />
              ))}
            </ul>
          </div>
          {log.hasNextPage ? (
            <Button
              variant="outline"
              onClick={() => void log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
            >
              {log.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
